import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { loginUser, reduxUpdateUserDetail, VerifyUserOtp } from "../../redux/auth/auth";
import { cleanObject } from "../../components/cleanObject";
import { useDocumentTitle } from "../../utils/Common";
import AccessibilityMenu from "../../components/AccessibilityMenu";
import { useTheme } from "../../utils/theme";
import { useLocation } from "react-router-dom";
import { getAuthenticatedRedirectPath } from "../../utils/authRedirect";
import states from "../state.json";

function Login() {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth.authState);
  const userDetail = useSelector((state) => state.auth.userDetail);
  const { theme } = useTheme();
  const [loginMode, setLoginMode] = useState("mobile");
  const [showOtp, setShowOtp] = useState(true);
  const [timer, setTimer] = useState(30);
  const { search } = useLocation();
  const sr = new URLSearchParams(search).get("sr");
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUserDetails, setNewUserDetails] = useState({
    firstName: "",
    email: "",
    mobile: "",
    state: "",
  });
  const [hasLogin, setHasLogin] = useState({
    countryCode: "+91",
    mobile: "",
    email: "",
    otp: "",
    otpVia: "text",
    referredBy: "",
    loginVia: "clientWeb",
  });

  useDocumentTitle("Sign-in");

  const handleChange = (event) => {
    const { name, value } = event.target;
    const cleanValue = name === "mobile" || name === "otp" ? value.replace(/\D/g, "") : value;

    setHasLogin((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));
  };

  const handleNewUserDetailChange = (event) => {
    const { name, value } = event.target;
    const cleanValue = name === "mobile" ? value.replace(/\D/g, "") : value;

    setNewUserDetails((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));
  };

  const resetNewUserDetails = () => {
    setIsNewUser(false);
    setNewUserDetails({
      firstName: "",
      email: "",
      mobile: "",
      state: "",
    });
  };

  const handleLoginModeChange = (mode) => {
    setLoginMode(mode);
    setShowOtp(true);
    setTimer(30);
    resetNewUserDetails();
    setHasLogin((prev) => ({
      ...prev,
      mobile: "",
      email: "",
      otp: "",
      otpVia: "text",
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const hasValue = (value) => String(value ?? "").trim().length > 0;

  const getResponseMessage = (response) => {
    if (typeof response?.payload === "string") {
      return response.payload;
    }

    return response?.payload?.message || response?.payload?.data?.message;
  };

  const getResponseUser = (payload) => {
    return payload?.data?.data || payload?.data?.user || payload?.data || payload?.user || {};
  };

  const getExplicitNewUserFlag = (payload) => {
    const data = payload?.data || {};
    const user = getResponseUser(payload);
    const existingFlag = (value) => (value === undefined ? undefined : value === false);

    return [
      payload?.isNewUser ??
      payload?.isNew ??
      payload?.newUser ??
      existingFlag(payload?.isExistingUser) ??
      existingFlag(payload?.userExists) ??
      data?.isNewUser ??
      data?.isNew ??
      data?.newUser ??
      existingFlag(data?.isExistingUser) ??
      existingFlag(data?.userExists) ??
      user?.isNewUser ??
      user?.isNew ??
      user?.newUser
    ][0];
  };

  const needsDetails = (user, mode = loginMode) => {
    if (!user) {
      return false;
    }

    return (
      !hasValue(user.firstName) ||
      !hasValue(user.state) ||
      (mode === "profile" && (!hasValue(user.email) || !hasValue(user.mobile))) ||
      (mode === "mobile" && !hasValue(user.email)) ||
      (mode === "email" && !hasValue(user.mobile))
    );
  };

  const shouldShowDetailsAfterVerify = (payload) => {
    const explicitFlag = getExplicitNewUserFlag(payload);

    if (explicitFlag !== undefined) {
      return Boolean(explicitFlag);
    }

    return needsDetails(getResponseUser(payload));
  };

  const fillNewUserDetails = (user = {}) => {
    setNewUserDetails((prev) => ({
      ...prev,
      firstName: user.firstName || prev.firstName,
      email: user.email || hasLogin.email || prev.email,
      mobile: user.mobile || hasLogin.mobile || prev.mobile,
      state: user.state || prev.state,
    }));
  };

  const getLoginPayload = (includeOtp = false) => {
    const payload = cleanObject(hasLogin);

    if (loginMode === "email") {
      delete payload.mobile;
      delete payload.countryCode;
      delete payload.otpVia;
    } else {
      delete payload.email;
    }

    if (!includeOtp) {
      delete payload.otp;
    }

    return payload;
  };

  const handleSendOtp = async () => {
    if (loginMode === "mobile" && hasLogin.mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (loginMode === "email" && !isValidEmail(hasLogin.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const response = await dispatch(loginUser(getLoginPayload()));

    if (loginUser.fulfilled.match(response)) {
      toast.success(response?.payload?.message);
      setNewUserDetails((prev) => ({
        ...prev,
        email: loginMode === "email" ? hasLogin.email : "",
        mobile: loginMode === "mobile" ? hasLogin.mobile : "",
      }));
      setShowOtp(false);
      setTimer(30);
    }
  };

  const validateNewUserDetails = () => {
    if (!newUserDetails.firstName.trim()) {
      toast.error("Please enter your name.");
      return false;
    }

    if (!newUserDetails.state.trim()) {
      toast.error("Please enter your state.");
      return false;
    }

    if (loginMode === "mobile" && !isValidEmail(newUserDetails.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    if (loginMode === "email" && newUserDetails.mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return false;
    }

    return true;
  };

  const getDetailsPayload = () => {
    return cleanObject({
      firstName: newUserDetails.firstName.trim(),
      email: newUserDetails.email || hasLogin.email || userDetail?.email,
      mobile: newUserDetails.mobile || hasLogin.mobile || userDetail?.mobile,
      state: newUserDetails.state.trim(),
      source: sr || "login",
    });
  };

  const redirectAfterAuth = (nextUserDetail = userDetail) => {
    setHasLogin({
      countryCode: "+91",
      mobile: "",
      email: "",
      otp: "",
      otpVia: "text",
      referredBy: "",
      loginVia: "clientWeb",
    });
    resetNewUserDetails();
    window.location.href = getAuthenticatedRedirectPath(nextUserDetail)
  };

  const handleVerifyOtp = async () => {
    if (hasLogin.otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP.");
      return;
    }

    if (isNewUser) {
      toast.error("Please submit your details.");
      return;
    }

    const verifyPayload = getLoginPayload(true);
    delete verifyPayload.otpVia;
    delete verifyPayload.loginVia;

    const response = await dispatch(VerifyUserOtp(verifyPayload));

    if (VerifyUserOtp.fulfilled.match(response)) {
      toast.success(getResponseMessage(response));

      if (shouldShowDetailsAfterVerify(response.payload)) {
        setIsNewUser(true);
        fillNewUserDetails(getResponseUser(response.payload));
        toast.success("Please complete your details.");
        return;
      }

      redirectAfterAuth(getResponseUser(response.payload));
      return;
    }

    toast.error(getResponseMessage(response) || "OTP verification failed.");
  };

  const handleSubmitDetails = async () => {
    if (!validateNewUserDetails()) {
      return;
    }

    const response = await dispatch(reduxUpdateUserDetail(getDetailsPayload()));

    if (reduxUpdateUserDetail.fulfilled.match(response)) {
      toast.success(getResponseMessage(response) || "Details submitted successfully.");
      redirectAfterAuth({
        ...userDetail,
        ...getDetailsPayload(),
      });
      return;
    }

    toast.error(getResponseMessage(response) || "Unable to submit details.");
  };

  useEffect(() => {
    if (!authState) {
      setShowOtp(true);
      resetNewUserDetails();
      return;
    }

    const isProfileComplete = (
      String(userDetail?.firstName ?? "").trim() &&
      String(userDetail?.state ?? "").trim() &&
      String(userDetail?.email ?? "").trim() &&
      String(userDetail?.mobile ?? "").trim()
    );

    if (!userDetail || isProfileComplete) {
      return;
    }

    if (String(userDetail?.email ?? "").trim() && !String(userDetail?.mobile ?? "").trim()) {
      setLoginMode("email");
    } else {
      setLoginMode("mobile");
    }

    setShowOtp(false);
    setIsNewUser(true);
    setNewUserDetails((prev) => ({
      ...prev,
      firstName: userDetail.firstName || prev.firstName,
      email: userDetail.email || prev.email,
      mobile: userDetail.mobile || prev.mobile,
      state: userDetail.state || prev.state,
    }));
  }, [authState, userDetail]);

  useEffect(() => {
    if (timer <= 0 || showOtp) {
      return undefined;
    }

    const countdown = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(countdown);
  }, [showOtp, timer]);

  const maskedMobile = `${hasLogin.mobile?.slice(0, 2)}${"*".repeat(
    Math.max(hasLogin.mobile.length - 4, 0),
  )}${hasLogin.mobile?.slice(-2)}`;
  const maskedEmail = hasLogin.email
    ? `${hasLogin.email.slice(0, 2)}****${hasLogin.email.slice(hasLogin.email.indexOf("@"))}`
    : "";
  const maskedLoginValue = loginMode === "email" ? maskedEmail : maskedMobile;

  return (
    <section
      id="create-portfolio"
      className="bg-opacity-10 position-relative login-screen"
      style={{
        backgroundColor: theme === "dark" ? "#000000" : ""
      }}
    >
      <div className="position-absolute top-0 end-0 p-3 p-md-4 login-accessibility-toggle">
        <AccessibilityMenu />
      </div>

      <div className="container login-screen-container d-flex align-items-center justify-content-center">
        <div className="row g-4 justify-content-between align-items-center w-100 login-screen-row">
          <div className="col-lg-7 login-screen-branding">
            <a href="./">
              <img
                src={
                  theme === "dark"
                    ? "https://equitypandit.in/ep-wealth/25.svg"
                    : "https://equitypandit.in/ep-imgs/ep-advisor-logo.svg"
                }
                alt="EquityPandit Logo"
                className="img-fluid login-screen-logo"
              />
            </a>
            <h1 className="fw-bold my-4 my-lg-5 text-body-emphasis login-screen-title">
              Let&apos;s begin your journey towards wealth creation
            </h1>
          </div>

          <div className="col-lg-4">
            {/* <div className="rounded-3 shadow-sm p-3 p-sm-4 bg-white "> */}
            <div
              className="rounded-3 shadow-lg p-3 p-sm-4"
              style={{
                backgroundColor: theme === "dark" ? "#0A0A0B " : ""
              }}
            >
              <h3 className="fw-bold text-body-emphasis">Hello,</h3>
              <p className="text-body-secondary">Let&apos;s Get Started</p>

              {showOtp ? (
                <form
                  className="py-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSendOtp();
                  }}
                >
                  <div className="btn-group mb-3" role="group" aria-label="Select login method">
                    <button
                      type="button"
                      className={`btn btn-sm px-3 py-1 ${loginMode === "mobile" ? "auth-primary-btn" : "btn-outline-dark"}`}
                      onClick={() => handleLoginModeChange("mobile")}
                    >
                      Phone
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm px-3 py-1 ${loginMode === "email" ? "auth-primary-btn" : "btn-outline-dark"}`}
                      onClick={() => handleLoginModeChange("email")}
                    >
                      Email
                    </button>
                  </div>

                  {loginMode === "mobile" ? (
                    <>
                      <div className="form-floating login-form-floating">
                        <input
                          type="tel"
                          className="form-control fw-bold login-form-control"
                          id="floatingMobile"
                          placeholder="1005000"
                          name="mobile"
                          maxLength={10}
                          inputMode="numeric"
                          required
                          value={hasLogin.mobile}
                          onChange={handleChange}
                          autoFocus
                        />
                        <label htmlFor="floatingMobile">Enter Your Mobile Number</label>
                      </div>

                      <p className="d-block my-2 text-body-secondary">Get OTP on</p>
                      <div className="d-flex gap-2 login-action-row">
                        <button
                          onClick={() => setHasLogin({ ...hasLogin, otpVia: "text" })}
                          type="submit"
                          className="btn auth-primary-btn btn-lg w-100 mt-2 rounded-2"
                        >
                          <i className="fa-regular fa-comment"></i> SMS
                        </button>
                        <button
                          onClick={() => setHasLogin({ ...hasLogin, otpVia: "whatsapp" })}
                          type="submit"
                          className="btn auth-primary-btn btn-lg w-100 mt-2 rounded-2"
                        >
                          <i className="fa-brands fa-whatsapp"></i> Whatsapp
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-floating login-form-floating">
                        <input
                          type="email"
                          className="form-control fw-bold login-form-control"
                          id="floatingEmail"
                          placeholder="name@example.com"
                          name="email"
                          required
                          value={hasLogin.email}
                          onChange={handleChange}
                          autoFocus
                        />
                        <label htmlFor="floatingEmail">Enter Your Email</label>
                      </div>

                      <button
                        type="submit"
                        className="btn auth-primary-btn btn-lg w-100 mt-3 rounded-2"
                      >
                        Get Code
                      </button>
                    </>
                  )}
                </form>
              ) : (
                <form
                  className="py-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (isNewUser) {
                      handleSubmitDetails();
                      return;
                    }

                    handleVerifyOtp();
                  }}
                >
                  {!isNewUser && (
                    <>
                      {/* <label className="text-body-emphasis form-label fw-bold mt-3 d-flex justify-content-center text-center login-otp-label">
                        A 4 digit OTP has been sent to {maskedMobile}
                        <FontAwesomeIcon
                          icon={faPencil}
                          className="border ms-2 p-1 rounded"
                          onClick={() => {
                            setShowOtp(true);
                            setTimer(30);
                          }}
                        />
                      </label> */}
                      <label className="form-label fw-bold mt-3 w-100">
                        <span className="d-flex align-items-start justify-content-center gap-2 text-center login-otp-message">
                          <span className="login-otp-message-text">
                            A 4 digit OTP has been sent to{" "}
                            <span className="login-otp-target">{maskedLoginValue}</span>
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary login-otp-edit"
                            aria-label="Edit login detail"
                            onClick={() => {
                              setShowOtp(true);
                              setTimer(30);
                              resetNewUserDetails();
                            }}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                        </span>
                      </label>

                      <div className="form-floating login-form-floating">
                        <input
                          type="tel"
                          className="form-control fw-bold mt-2 login-form-control"
                          id="floatingOtp"
                          placeholder="****"
                          name="otp"
                          maxLength={4}
                          inputMode="numeric"
                          required
                          value={hasLogin.otp}
                          onChange={handleChange}
                          autoFocus
                        />
                        <label htmlFor="floatingOtp">Enter OTP</label>
                      </div>
                    </>
                  )}

                  {isNewUser && (
                    <div className="">
                      <p className="fw-bold text-body-emphasis mb-2">Complete your details</p>

                      <div className="form-floating login-form-floating mb-2">
                        <input
                          type="text"
                          className="form-control fw-bold login-form-control"
                          id="floatingName"
                          placeholder="Your name"
                          name="firstName"
                          required
                          value={newUserDetails.firstName}
                          onChange={handleNewUserDetailChange}
                        />
                        <label htmlFor="floatingName">Name</label>
                      </div>

                      {loginMode === "mobile" ? (
                        <div className="form-floating login-form-floating mb-2">
                          <input
                            type="email"
                            className="form-control fw-bold login-form-control"
                            id="floatingNewEmail"
                            placeholder="name@example.com"
                            name="email"
                            required
                            value={newUserDetails.email}
                            onChange={handleNewUserDetailChange}
                          />
                          <label htmlFor="floatingNewEmail">Email</label>
                        </div>
                      ) : (
                        <div className="form-floating login-form-floating mb-2">
                          <input
                            type="tel"
                            className="form-control fw-bold login-form-control"
                            id="floatingNewMobile"
                            placeholder="1005000"
                            name="mobile"
                            maxLength={10}
                            inputMode="numeric"
                            required
                            value={newUserDetails.mobile}
                            onChange={handleNewUserDetailChange}
                          />
                          <label htmlFor="floatingNewMobile">Mobile Number</label>
                        </div>
                      )}

                      <div className="form-floating login-form-floating">
                        <select
                          className="form-select fw-bold login-form-control"
                          id="floatingState"
                          name="state"
                          required
                          value={newUserDetails.state}
                          onChange={handleNewUserDetailChange}
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.text}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="floatingState">State</label>
                      </div>

                      <button
                        type="submit"
                        className="btn auth-primary-btn btn-lg w-100 mt-3"
                      >
                        <span className="d-flex align-items-center justify-content-center">
                          <span>Submit Details</span>
                        </span>
                      </button>
                    </div>
                  )}

                  {!isNewUser && (
                    <>
                      <div className="text-muted mt-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-body-emphasis">
                            {timer > 0 && `00:${timer.toString().padStart(2, "0")}`}
                          </span>
                          {timer <= 0 && (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-body-emphasis text-decoration-underline"
                              onClick={() => {
                                setTimer(30);
                                handleSendOtp();
                              }}
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-2">
                        <button type="submit" className="btn auth-primary-btn btn-lg w-100 mt-2">
                          <span className="d-flex align-items-center justify-content-center">
                            <span>Verify OTP</span>
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;
